<template>
  <div class="app-container">
    <el-card class="box-card">
      <div slot="header">
        DeepSeek 月度用量
        <el-date-picker v-model="usageMonth" type="month" size="mini" style="width:140px; float:right" value-format="yyyy-MM" @change="loadUsage" />
      </div>
      <el-descriptions :column="3" size="small" border v-loading="usageLoading">
        <el-descriptions-item label="对话调用次数">{{ usage.chatCallCount || 0 }}</el-descriptions-item>
        <el-descriptions-item label="对话输入 Tokens">{{ usage.chatPromptTokens || 0 }}</el-descriptions-item>
        <el-descriptions-item label="对话输出 Tokens">{{ usage.chatCompletionTokens || 0 }}</el-descriptions-item>
        <el-descriptions-item label="评分调用次数">{{ usage.evalCallCount || 0 }}</el-descriptions-item>
        <el-descriptions-item label="评分 Tokens">{{ usage.evalTotalTokens || 0 }}</el-descriptions-item>
        <el-descriptions-item label="合计 Tokens">{{ usage.grandTotalTokens || 0 }}</el-descriptions-item>
      </el-descriptions>
    </el-card>

    <el-card class="box-card mt10">
      <el-alert v-if="query.sceneId" :title="'本场景正式训练记录：' + (scopeSceneTitle || query.sceneId) + '（不含管理端试聊）'" type="info" :closable="false" class="mb10" />
      <el-form :inline="true" :model="query" size="small">
        <el-form-item label="人员/关键字">
          <el-input v-model="query.keyword" placeholder="人员/场景标题" clearable style="width:160px" />
        </el-form-item>
        <el-form-item label="模式">
          <el-select v-model="query.mode" clearable placeholder="全部" style="width:100px">
            <el-option :value="1" label="练习" />
            <el-option :value="2" label="考试" />
          </el-select>
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="query.status" clearable placeholder="全部" style="width:120px">
            <el-option :value="1" label="进行中" />
            <el-option :value="2" label="待评分" />
            <el-option :value="3" label="已评分" />
            <el-option :value="4" label="评分失败" />
            <el-option :value="5" label="已放弃" />
          </el-select>
        </el-form-item>
        <el-form-item label="通关">
          <el-select v-model="query.passed" clearable placeholder="全部" style="width:100px">
            <el-option :value="true" label="通关" />
            <el-option :value="false" label="未通关" />
          </el-select>
        </el-form-item>
        <el-form-item label="时间范围">
          <el-date-picker v-model="dateRange" type="daterange" size="small" value-format="yyyy-MM-dd" start-placeholder="开始" end-placeholder="结束" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleQuery">查询</el-button>
          <el-button @click="handleReset">重置</el-button>
          <el-button type="warning" plain @click="handleExport" v-hasPermi="['aiTrain:record:export']">导出</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <el-card class="box-card mt10">
      <el-table v-loading="listLoading" :data="list" border>
        <el-table-column prop="userName" label="人员" width="100" />
        <el-table-column prop="deptName" label="部门" width="120" />
        <el-table-column prop="sceneTitle" label="场景" min-width="160" show-overflow-tooltip />
        <el-table-column label="模式" width="80">
          <template slot-scope="{row}"><el-tag size="mini" :type="row.mode === 2 ? 'warning' : 'success'">{{ row.mode === 2 ? '考试' : '练习' }}</el-tag></template>
        </el-table-column>
        <el-table-column label="总分" width="80">
          <template slot-scope="{row}">{{ row.totalScore != null ? row.totalScore : '-' }}</template>
        </el-table-column>
        <el-table-column label="通关" width="80">
          <template slot-scope="{row}">
            <el-tag v-if="row.passed === true" type="success" size="mini">通关</el-tag>
            <el-tag v-else-if="row.passed === false" type="info" size="mini">未通关</el-tag>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column label="用时" width="90">
          <template slot-scope="{row}">{{ formatDuration(row.elapsedSec) }}</template>
        </el-table-column>
        <el-table-column label="状态" width="90">
          <template slot-scope="{row}">{{ statusLabel(row.status) }}</template>
        </el-table-column>
        <el-table-column label="时间" width="180">
          <template slot-scope="{row}">{{ formatRecordTime(row.createdAt || row.createdTime) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="165" fixed="right">
          <template slot-scope="{row}">
            <el-button type="text" size="mini" @click="$refs.recordDetail.open(row)">查看详情</el-button>
            <el-button v-if="row.status === 4" type="text" size="mini" @click="handleReEvaluate(row)" v-hasPermi="['aiTrain:record:reEvaluate']">重新评分</el-button>
          </template>
        </el-table-column>
      </el-table>
      <pagination v-show="total>0" :total="total" :pageNo.sync="query.pageNo" :pageSize.sync="query.pageSize" @pagination="onPagination" />
    </el-card>

    <el-card class="box-card mt10" v-if="exportTasks.length">
      <div slot="header">导出任务</div>
      <el-table :data="exportTasks" size="mini" border>
        <el-table-column prop="id" label="任务ID" width="100" />
        <el-table-column prop="status" label="状态" width="100">
          <template slot-scope="{row}">{{ { 1: '排队中', 2: '生成中', 3: '已完成', 4: '失败' }[row.status] || row.status }}</template>
        </el-table-column>
        <el-table-column prop="createdTime" label="创建时间" width="180">
          <template slot-scope="{row}">{{ formatRecordTime(row.createdTime) }}</template>
        </el-table-column>
        <el-table-column prop="errorMsg" label="失败原因" min-width="160" show-overflow-tooltip />
        <el-table-column label="操作" width="100">
          <template slot-scope="{row}">
            <el-button v-if="row.status === 3 && row.downloadUrl" type="text" :loading="downloadingId === row.id" @click="downloadExport(row)">下载</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>
    <record-detail ref="recordDetail" />
  </div>
</template>

<script>
import { listRows } from '@/utils/ai-train-data'
import aiTrainRecordApi from '@/api/aiTrain/record'
import Pagination from '@/components/Pagination'
import RecordDetail from './RecordDetail'
import { formatRecordTime } from '@/utils/ai-train-record'

export default {
  name: 'AiTrainRecord',
  components: { Pagination, RecordDetail },
  data() {
    return {
      query: { keyword: '', mode: undefined, status: undefined, passed: undefined, startDate: undefined, endDate: undefined, pageNo: 1, pageSize: 10 },
      scopeSceneTitle: '',
      dateRange: [],
      list: [],
      total: 0,
      listLoading: false,
      usageMonth: this.currentMonth(),
      usage: {},
      usageLoading: false,
      exportTasks: [],
      downloadingId: null,
      exportPollTimer: null,
      exportTasksRequest: null,
      exportDisposed: false
    }
  },
  created() {
    this.applySceneScope()
    this.loadList()
    this.loadUsage()
    this.loadExportTasks()
  },
  watch: {
    '$route.query.sceneId'() {
      if (this.$route.path !== '/aiTrain/record') return
      this.applySceneScope()
      this.loadList()
    }
  },
  beforeDestroy() {
    this.exportDisposed = true
    this.stopExportPolling()
  },
  methods: {
    formatRecordTime,
    applySceneScope() {
      const id = Number(this.$route.query.sceneId)
      if (Number.isSafeInteger(id) && id > 0) this.$set(this.query, 'sceneId', id)
      else this.$delete(this.query, 'sceneId')
      this.scopeSceneTitle = this.$route.query.sceneTitle || ''
      this.query.pageNo = 1
    },
    onPagination({ pageNo, pageSize }) {
      this.query.pageNo = pageSize === this.query.pageSize ? pageNo : 1
      this.query.pageSize = pageSize
      this.loadList()
    },
    currentMonth() {
      const d = new Date()
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    },
    statusLabel(v) {
      return { 1: '进行中', 2: '待评分', 3: '已评分', 4: '评分失败', 5: '已放弃' }[v] || '-'
    },
    formatDuration(sec) {
      if (!sec && sec !== 0) return '-'
      const m = Math.floor(sec / 60)
      const s = sec % 60
      return `${m}分${s}秒`
    },
    handleQuery() {
      this.query.pageNo = 1
      if (this.dateRange && this.dateRange.length === 2) {
        this.query.startDate = this.dateRange[0]
        this.query.endDate = this.dateRange[1]
      } else {
        this.query.startDate = undefined
        this.query.endDate = undefined
      }
      this.loadList()
    },
    handleReset() {
      const sceneId = this.query.sceneId
      this.query = { keyword: '', mode: undefined, status: undefined, passed: undefined, startDate: undefined, endDate: undefined, pageNo: 1, pageSize: 10 }
      if (sceneId) this.$set(this.query, 'sceneId', sceneId)
      this.dateRange = []
      this.loadList()
    },
    loadList() {
      this.listLoading = true
      aiTrainRecordApi.fetchList(this.query)
        .then(res => {
          const data = (res && res.data) || res || {}
          this.list = listRows(data)
          this.total = data.total || this.list.length
        })
        .catch(() => { this.list = []; this.total = 0 })
        .finally(() => { this.listLoading = false })
    },
    loadUsage() {
      this.usageLoading = true
      aiTrainRecordApi.fetchMonthlyUsage(this.usageMonth)
        .then(res => { this.usage = (res && res.data) || res || {} })
        .catch(() => { this.usage = {} })
        .finally(() => { this.usageLoading = false })
    },
    handleReEvaluate(row) {
      this.$common.confirm('确认对该会话重新评分？').then(() => aiTrainRecordApi.reEvaluate(row.sessionId || row.id))
        .then(() => {
          this.$message.success('已重新提交评分')
          this.loadList()
        })
    },
    handleExport() {
      // 导出取当前表单的日期范围，避免未点查询时沿用旧日期。
      this.query.startDate = this.dateRange && this.dateRange.length === 2 ? this.dateRange[0] : undefined
      this.query.endDate = this.dateRange && this.dateRange.length === 2 ? this.dateRange[1] : undefined
      return aiTrainRecordApi.export(this.query).then(() => {
        if (this.exportDisposed) return
        this.$message.success('导出任务已提交，见下方导出任务列表')
        // 等待可能早于提交发出的查询，再取任务，避免旧响应漏掉刚创建的导出。
        return Promise.resolve(this.exportTasksRequest).then(() => this.loadExportTasks())
      }).catch(() => {})
    },
    downloadExport(row) {
      this.downloadingId = row.id
      return aiTrainRecordApi.download(row.id).then(blob => {
        if (this.exportDisposed) return
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `ai_train_record_${row.id}.csv`
        document.body.appendChild(link)
        link.click()
        link.remove()
        setTimeout(() => URL.revokeObjectURL(url), 1000)
      }).catch(() => {}).finally(() => { if (!this.exportDisposed) this.downloadingId = null })
    },
    stopExportPolling() {
      if (this.exportPollTimer !== null) clearInterval(this.exportPollTimer)
      this.exportPollTimer = null
    },
    loadExportTasks() {
      if (this.exportDisposed) return Promise.resolve()
      // 慢请求期间复用同一个 Promise，轮询与手动刷新不会重叠或乱序覆盖。
      if (this.exportTasksRequest) return this.exportTasksRequest
      this.exportTasksRequest = aiTrainRecordApi.fetchExportTasks()
        .then(res => {
          if (this.exportDisposed) return
          const data = (res && res.data) || res || []
          this.exportTasks = listRows(data)
          if (this.exportTasks.some(t => t.status === 1 || t.status === 2)) {
            if (this.exportPollTimer === null) {
              this.exportPollTimer = setInterval(() => this.loadExportTasks(), 5000)
            }
          } else {
            this.stopExportPolling()
          }
        })
        .catch(() => {})
        .finally(() => { this.exportTasksRequest = null })
      return this.exportTasksRequest
    }
  }
}
</script>

<style scoped>
.mt10 { margin-top: 10px; }
</style>
